"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { BrandLogo } from "@/components/marketing/logo";
import { cabinetOr, primaryLink, primaryLinkCompact, sectionLinks, shell } from "@/components/marketing/shared";

export function MarketingHeader({ signedIn }: { signedIn: boolean }) {
  const pathname = usePathname();
  const [openPath, setOpenPath] = useState<string | null>(null);
  const open = openPath === pathname;
  const login = signedIn ? { href: "/app", label: "Кабинет" } : { href: "/login", label: "Войти" };
  const cta = cabinetOr(signedIn, { href: "/register", label: "Попробовать бесплатно" });

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenPath(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--m-line)] bg-white">
      <div className="relative">
        <div className={`${shell} flex h-14 items-center justify-between gap-4`}>
          <div className="flex min-w-0 items-center gap-8">
            <Link href="/" className="inline-flex items-center">
              <BrandLogo className="h-7 w-auto" />
            </Link>
            <nav className="hidden items-center gap-7 lg:flex" aria-label="Разделы">
              {sectionLinks.map((link) => {
                const active = link.href === "/pricing" && pathname === "/pricing";
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    className={
                      active
                        ? "relative text-sm font-semibold text-[var(--m-ink)] after:absolute after:inset-x-0 after:-bottom-3.5 after:h-0.5 after:rounded-full after:bg-[var(--m-accent)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--m-accent)]"
                        : "text-sm font-medium text-[#3d4b60] transition-colors hover:text-[var(--m-ink)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--m-accent)]"
                    }
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="hidden items-center gap-5 lg:flex">
            <Link
              href={login.href}
              className="inline-flex h-10 items-center text-sm font-semibold text-[var(--m-ink)] transition-colors hover:text-[var(--m-accent)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--m-accent)]"
            >
              {login.label}
            </Link>
            <Link href={cta.href} className={primaryLinkCompact}>
              {cta.label}
            </Link>
          </div>
          <button
            type="button"
            className="inline-flex size-11 items-center justify-center rounded-lg text-[var(--m-ink)] hover:bg-[var(--m-soft)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--m-accent)] lg:hidden"
            aria-expanded={open}
            aria-controls="marketing-menu"
            onClick={() => setOpenPath(open ? null : pathname)}
          >
            {open ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
            <span className="sr-only">{open ? "Закрыть меню" : "Открыть меню"}</span>
          </button>
        </div>
        {open ? (
          <div id="marketing-menu" className="absolute inset-x-0 top-full border-b border-[var(--m-line)] bg-white px-5 py-3 shadow-[0_16px_30px_-24px_rgba(16,24,40,0.45)] lg:hidden">
            <nav className="mx-auto flex w-full max-w-[1180px] flex-col" aria-label="Меню">
              {sectionLinks.map((link) => {
                const active = link.href === "/pricing" && pathname === "/pricing";
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    className={`flex min-h-12 items-center text-base ${active ? "font-semibold text-[var(--m-ink)]" : "font-medium text-[var(--m-ink)]"}`}
                    onClick={() => setOpenPath(null)}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                href={login.href}
                className="flex min-h-12 items-center text-base font-medium text-[var(--m-ink)]"
                onClick={() => setOpenPath(null)}
              >
                {login.label}
              </Link>
              <Link href={cta.href} className={`${primaryLink} mt-2 w-full`} onClick={() => setOpenPath(null)}>
                {cta.label}
              </Link>
            </nav>
          </div>
        ) : null}
      </div>
    </header>
  );
}
